import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { Tooltip } from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';

import { VatixError } from 'components/Error/types';

import { useStore } from 'utils/hooks/store';
import { UuidableName } from 'utils/api/types';
import { UserListItemResponse } from 'utils/api/modules/user/user.types';
import ResponseProfileCell from 'containers/Manage/components/ResponseProfileCell';
import routes from 'core/routes';
import { UserRole } from 'core/constants';
import Grid from 'components/Grid/Grid';
import { ColumnDefinition } from 'components/Grid/types';
import UserCell from 'containers/Users/components/UserCell';
import Search from 'components/Grid/operators/Search';
import API from 'utils/api';

import { colors } from 'theme/colors';

import MonitoredDevicesCell from '../MonitoredDevicesCell';
import ArcSelectorCell from '../ArcSelectorCell';
import { OrganisationArcsContext, useOrganisationArcs } from '../ArcSelectorCell/organisationArcs';
import { ColumnHeader, InfoContainer, InfoDescription } from './styles';

const ManageList: React.FunctionComponent<{
  onRefreshCallback: (callback: () => void) => void;
  onError: (error: VatixError) => void;
}> = ({ onError, onRefreshCallback }): React.ReactElement => {
  const { routing, session } = useStore();
  const showArcColumn = session.user?.securitasMastermindEnabled ?? false;
  const organisationArcs = useOrganisationArcs(showArcColumn, session.user?.role === UserRole.Admin);

  const onRowClick: GridEventListener<'rowClick'> = ({ row: { uuid } }: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.settings.users.details, { userId: uuid }));
  };

  const basicColumns = [
    {
      field: 'name',
      headerName: 'Name',
      type: 'string',
      valueGetter: (_value: unknown, row: UserListItemResponse): UuidableName => ({
        uuid: row.uuid,
        name: row.name,
      }),
      renderCell: ({ value }: { value: { name: string; uuid: string } }) => (
        <UserCell name={value.name} uuid={value.uuid} />
      ),
      nativeFilter: 'query',
      flex: 2,
    },
    {
      field: 'alarmResponseProfile',
      headerName: 'Alarm response profile',
      valueGetter: (_value: unknown, row: UserListItemResponse) => row,
      renderCell: ({ row, colDef }: { row: UserListItemResponse; colDef: ColumnDefinition }) => (
        <ResponseProfileCell rowData={row} actions={colDef.actions} />
      ),
      filterOperators: Search(API.alarms.searchAlarmResponseProfiles, API.alarms.loadEscalationProfileDetails, 'name'),
      nativeFilter: 'alarm_response_profiles',
      flex: 2,
      renderHeader: () => (
        <ColumnHeader>
          Alarm response profile
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  boxShadow: `0px 3px 14px 2px ${colors.divider}, 0px 8px 10px 1px ${colors.grey[300]}, 0px 5px 5px -3px ${colors.grey[400]}`,
                  borderRadius: '4px',
                },
              },
            }}
            title={
              <InfoContainer>
                <InfoDescription>
                  Vatix 24/7 Monitoring: if your subscription includes access to the Vatix Alarm Receiving Centre.
                  Self-Monitoring if you are monitoring alarms internally.
                </InfoDescription>
              </InfoContainer>
            }
            placement="bottom"
          >
            <InfoIcon fontSize="small" sx={{ marginLeft: '10px', color: colors.grey[400] }} />
          </Tooltip>
        </ColumnHeader>
      ),
      // ToDo: implement sorting on backend
      sortable: false,
    },
    ...(showArcColumn
      ? [
          {
            field: 'arc',
            headerName: 'ARC',
            renderHeader: () => <ColumnHeader data-testid="manage-arc-column-header">ARC</ColumnHeader>,
            renderCell: ({ row, colDef }: { row: UserListItemResponse; colDef: ColumnDefinition }) => (
              <ArcSelectorCell rowData={row} actions={colDef.actions} />
            ),
            filterable: false,
            sortable: false,
            flex: 2,
          },
        ]
      : []),
    {
      field: 'licenses',
      headerName: 'Products',
      valueGetter: (data: { row: UserListItemResponse }) => data.row,
      renderCell: ({ row, colDef }: { row: UserListItemResponse; colDef: ColumnDefinition }) => (
        <MonitoredDevicesCell rowData={row} actions={colDef.actions} />
      ),
      filterable: false,
      flex: 4,
      renderHeader: () => (
        <ColumnHeader>
          Products
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  boxShadow: `0px 3px 14px 2px ${colors.divider}, 0px 8px 10px 1px ${colors.grey[300]}, 0px 5px 5px -3px ${colors.grey[400]}`,
                  borderRadius: '4px',
                },
              },
            }}
            title={
              <InfoContainer>
                <InfoDescription>
                  Use the &apos;Mobile App&apos; switch to assign a mobile app licence to a user. Click &apos;Add
                  Device&apos; to assign a safety device to a user.
                </InfoDescription>
              </InfoContainer>
            }
            placement="bottom"
          >
            <InfoIcon fontSize="small" sx={{ marginLeft: '10px', color: colors.grey[400] }} />
          </Tooltip>
        </ColumnHeader>
      ),
      // ToDo: implement sorting on backend
      sortable: false,
    },
  ] as unknown as ColumnDefinition[];

  return (
    <OrganisationArcsContext.Provider value={organisationArcs}>
      <Grid
        basicColumns={basicColumns}
        entity="license"
        onError={onError}
        onRowClick={onRowClick}
        dataURL="/api/users/?licenses=protectorAlarms"
        onRefreshCallback={onRefreshCallback}
        sortBy={{ field: 'name', sort: 'asc' }}
        disableColumnSelector
      />
    </OrganisationArcsContext.Provider>
  );
};

export default observer(ManageList);
